import React, { useEffect } from 'react';
import useMatchMedia from '@dashboard/hooks/useMatchMedia';

const PreviewStateContext = React.createContext();
const PreviewDispatchContext = React.createContext();

function previewReducer(state, action) {
  switch (action.type) {
    case 'toggle': {
      return {
        ...state,
        open: !state.open,
      };
    }
    case 'open': {
      return {
        ...state,
        open: true,
      };
    }
    case 'close': {
      return {
        ...state,
        open: false,
      };
    }
    case 'set': {
      return {
        ...state,
        ...action.value,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function getVariantFromPage(page) {
  if (page.usageType === 'hosted') {
    return 'hosted_page';
  }

  if (page.usageType === 'site') {
    if (page.siteLayoutType === 'embed') {
      return 'site_embed';
    }

    if (page.siteLayoutType === 'popup') {
      return 'site_popup';
    }
  }

  return 'page';
}

function PreviewProvider({ page, children }) {
  const isSmall = useMatchMedia('(max-width: 768px)');

  const [state, dispatch] = React.useReducer(previewReducer, {
    open: isSmall ? false : true,
    type: 'checkout',
    variant: getVariantFromPage(page),
  });

  useEffect(() => {
    dispatch({ type: 'set', value: { variant: getVariantFromPage(page) } });
  }, [page.usageType, page.siteLayoutType, page.hostedLayoutType]);

  return (
    <PreviewStateContext.Provider value={state}>
      <PreviewDispatchContext.Provider value={dispatch}>{children}</PreviewDispatchContext.Provider>
    </PreviewStateContext.Provider>
  );
}

function usePreviewState() {
  const context = React.useContext(PreviewStateContext);

  if (context === undefined) {
    throw new Error('usePreviewState must be used within a PreviewProvider');
  }

  return context;
}

function usePreviewDispatch() {
  const context = React.useContext(PreviewDispatchContext);

  if (context === undefined) {
    throw new Error('usePreviewDispatch must be used within a PreviewProvider');
  }

  return context;
}

function usePreview() {
  return [usePreviewState(), usePreviewDispatch()];
}

export { PreviewProvider, usePreview };
